import styled, { keyframes } from 'styled-components';

export const DropOverlay = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: rgba(0, 0, 0, 0.7);
	border: 2px dashed ${({ theme }) => theme.gray[400]};
	border-radius: 8px;
	pointer-events: none;
	z-index: 10;
	font-size: 18px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[200]};
`;

const createHighlightFade = (color: string) =>
	keyframes`
		0%, 80% {
			box-shadow: 0 0 0 2px ${color};
		}
		100% {
			box-shadow: none;
		}
	`;

export const TitleCount = styled.span`
	font-size: 20px;
	font-weight: 400;
	color: ${({ theme }) => theme.gray[600]};
	margin-left: 6px;
`;

export const CategorySection = styled.div`
	margin-bottom: 36px;
`;

export const CategoryHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 16px;
`;

export const CategoryDot = styled.div<{ $color: string }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${({ $color }) => $color};
	flex-shrink: 0;
`;

export const CategoryLabel = styled.span`
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 1px;
	color: ${({ theme }) => theme.gray[600]};
`;

export const CategoryCount = styled.span`
	font-size: 12px;
	font-weight: 400;
	color: ${({ theme }) => theme.gray[400]};
`;

export const AssetGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(155px, 1fr));
	gap: 20px;
`;

export const AssetCard = styled.div<{ $selected?: boolean; $isNew?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 0;
	animation: ${({ theme, $isNew }) => ($isNew ? createHighlightFade(theme.blue[300]) : 'none')} 10s
		ease forwards;
`;

export const ChatButton = styled.button`
	position: absolute;
	top: 6px;
	right: 96px;
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	background-color: rgba(0, 0, 0, 0.6);
	color: ${({ theme }) => theme.gray[0]};
	border: none;
	opacity: 0;
	z-index: 2;
	transition: opacity 0.15s ease, background-color 0.15s ease;

	${AssetCard}:hover & {
		opacity: 1;
	}

	&:hover {
		background-color: ${({ theme }) => theme.blue[700]};
	}
`;

export const MoveButtonWrapper = styled.div`
	position: absolute;
	top: 6px;
	right: 66px;
	opacity: 0;
	transition: opacity 0.15s ease;
	z-index: 2;

	${AssetCard}:hover & {
		opacity: 1;
	}

	&:has([data-state='open']) {
		opacity: 1;
	}
`;

export const MoveButton = styled.button`
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	background-color: rgba(0, 0, 0, 0.6);
	color: ${({ theme }) => theme.gray[0]};
	border: none;
	transition: background-color 0.15s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[600]};
	}
`;

export const DownloadButton = styled.button`
	position: absolute;
	top: 6px;
	right: 6px;
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	background-color: rgba(0, 0, 0, 0.6);
	color: ${({ theme }) => theme.gray[0]};
	border: none;
	opacity: 0;
	z-index: 2;
	transition: opacity 0.15s ease, background-color 0.15s ease;

	${AssetCard}:hover & {
		opacity: 1;
	}

	&:hover {
		background-color: ${({ theme }) => theme.green[600]};
	}
`;

export const TrashButton = styled.button`
	position: absolute;
	top: 6px;
	right: 36px;
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	background-color: rgba(0, 0, 0, 0.6);
	color: ${({ theme }) => theme.gray[0]};
	border: none;
	opacity: 0;
	z-index: 2;
	transition: opacity 0.15s ease, background-color 0.15s ease;

	${AssetCard}:hover & {
		opacity: 1;
	}

	&:hover {
		background-color: ${({ theme }) => theme.red[500]};
	}
`;

export const AssetImageContainer = styled.div`
	width: 100%;
	aspect-ratio: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 6px;
	overflow: hidden;
`;

export const AssetImage = styled.img`
	max-width: 85%;
	max-height: 85%;
	object-fit: contain;
`;

export const AssetMimeType = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
`;

export const AssetName = styled.div`
	font-size: 13px;
	font-weight: 500;
	margin-top: 8px;
	color: ${({ theme }) => theme.gray[1000]};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const AssetPath = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-top: 1px;
`;

export const HeaderControls = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

export const ToolbarButton = styled.button`
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	font-weight: 500;
	padding: 6px 14px;
	border-radius: 6px;
	border: 1px solid ${({ theme }) => theme.gray[400]};
	background-color: transparent;
	color: ${({ theme }) => theme.gray[800]};
	transition: background-color 0.15s ease, border-color 0.15s ease;

	&:not(:disabled):hover {
		background-color: ${({ theme }) => theme.gray[200]};
		border-color: ${({ theme }) => theme.gray[600]};
	}

	&:disabled {
		opacity: 0.6;
	}
`;

// Table-style list view
export const AssetTable = styled.div`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	padding-top: 18px;
`;

const tableGridColumns = `minmax(200px, 2fr) minmax(150px, 2fr) 140px auto`;

export const AssetTableHeader = styled.div`
	display: grid;
	grid-template-columns: ${tableGridColumns};
	align-items: center;
	padding: 10px 16px;
	background-color: ${({ theme }) => theme.gray[100]};
	gap: 16px;
	border-radius: 12px 12px 0 0;
	height: 36px;
`;

export const AssetTableHeaderCell = styled.button<{ $sortable?: boolean }>`
	display: flex;
	align-items: center;
	gap: 4px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: ${({ theme }) => theme.gray[700]};
	background: none;
	border: none;
	padding: 0;
	user-select: none;
	transition: color 0.15s ease;

	${({ $sortable, theme }) =>
		$sortable &&
		`
		&:hover {
			color: ${theme.gray[1000]};
		}
	`}
`;

export const SortIndicator = styled.span<{ $active?: boolean }>`
	display: inline-flex;
	align-items: center;
	color: ${({ theme, $active }) => ($active ? theme.blue[500] : theme.gray[400])};
	opacity: ${({ $active }) => ($active ? 1 : 0.4)};
	transition: opacity 0.15s ease, color 0.15s ease;
`;

export const AssetTableRow = styled.div<{ $isNew?: boolean; $odd?: boolean }>`
	display: grid;
	grid-template-columns: ${tableGridColumns};
	align-items: center;
	padding: 10px 16px;
	gap: 16px;
	background-color: ${({ theme }) => theme.white};
	transition: background-color 0.15s ease;
	animation: ${({ theme, $isNew }) => ($isNew ? createHighlightFade(theme.blue[300]) : 'none')} 10s
		ease forwards;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
	}
`;

export const AssetTableActions = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 4px;
`;

export const AssetTableActionButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 4px;
	border: none;
	background-color: transparent;
	color: ${({ theme }) => theme.gray[400]};
	transition: background-color 0.15s ease, color 0.15s ease;

	&:hover {
		background-color: ${({ theme }) => theme.gray[200]};
		color: ${({ theme }) => theme.gray[1000]};
	}
`;

export const AssetTableNameCell = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
`;

export const AssetTableThumbnail = styled.div`
	width: 32px;
	height: 32px;
	min-width: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	overflow: hidden;
`;

export const AssetTableThumbnailImg = styled.img`
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
`;

export const AssetTableName = styled.div`
	font-size: 13px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[1000]};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const AssetTablePath = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.gray[600]};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const AssetTableCategorySelect = styled.select`
	font-size: 12px;
	padding: 4px 6px;
	border-radius: 4px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	background-color: transparent;
	color: ${({ theme }) => theme.gray[1000]};
	width: 100%;
`;

